import { useState } from 'react';
import PropTypes from 'prop-types';
import { Draggable, Droppable } from 'react-beautiful-dnd';

import { ActionOverlayPopover, Card, DragDropV2Icon, Layout, ShuffleIcon } from '@utilities';

import Question from '../Question';
import { useSurveyContext } from '../../../../../../utilities/context';

import styles from './_index.module.scss';

export const QUESTION_GROUP_DROPPABLE_PREFIX = 'question-group';
export const QUESTION_GROUP_DROPPABLE_TYPE = 'QUESTION_GROUP';

const QuestionGroupBlock = ({
  canPreviewSurvey,
  firstErrorRef,
  flatIndexStart,
  group,
  isDetailedQuestionView,
  onDuplicateQuestion,
  onEditQuestion,
  onPreviewSurvey,
  onRemoveQuestion,
  provided,
  questionLogic,
  questions,
  selectedQuestions,
  setQuestionLogic,
  showActions,
}) => {
  const {
    assignQuestionToGroup,
    isReadOnly,
    questionDisplayIndexById,
    renameGroup,
    toggleGroupRandomize,
  } = useSurveyContext();

  const [isDropdownActive, setIsDropdownActive] = useState(false);
  const [groupName, setGroupName] = useState(group.name);

  const droppableId = `${QUESTION_GROUP_DROPPABLE_PREFIX}-${group.id}`;
  const droppableType = `${QUESTION_GROUP_DROPPABLE_TYPE}-${group.id}`;
  const isRandomized = group.randomizeOrder ?? false;

  const handleNameBlur = () => {
    const trimmed = groupName.trim();
    if (trimmed && trimmed !== group.name) {
      renameGroup(group.id, trimmed);
    } else {
      // reset display if blank
      setGroupName(group.name);
    }
  };

  const handleRandomizeToggle = () => {
    toggleGroupRandomize(group.id);
  };

  const handleRemoveFromGroup = (question) => {
    // Unassign from group. If this was the last member, assignQuestionToGroup will
    // auto-DELETE the group via the context helper.
    assignQuestionToGroup(question, null);
  };

  /**
   * handleRemoveQuestionFromSurvey
   * When a question is fully removed from the survey (not just ungrouped), we need to
   * also clean up the group if it becomes empty. We call onRemoveQuestion (parent handles
   * the survey question deletion) and then check if the group is now empty.
   */
  const handleRemoveQuestionFromSurvey = (question) => {
    const remainingAfterRemoval = questions.filter((q) => q.id !== question.id);
    if (remainingAfterRemoval.length === 0) {
      // Last member — ungroup first so the context helper removes the group from local state.
      assignQuestionToGroup(question, null);
    }
    onRemoveQuestion(question);
  };

  return (
    <div
      className={styles['question-group-block-draggable-container']}
      ref={provided.innerRef}
      {...provided.draggableProps}
    >
      <Card
        as={Layout.Flex.Column}
        className={styles['question-group-block-card']}
        elevation={2}
        gap="small"
      >
        <div className={styles['question-group-block-header']}>
          <div className={styles['question-group-block-title-wrapper']}>
            <div
              className={styles['question-group-block-drag-handle']}
              {...provided.dragHandleProps}
            >
              <DragDropV2Icon />
            </div>
            <div className={styles['question-group-block-name-column']}>
              <input
                aria-label="Question group name"
                className={styles['question-group-block-name-input']}
                disabled={isReadOnly || !showActions}
                onBlur={handleNameBlur}
                onChange={(e) => setGroupName(e.target.value)}
                type="text"
                value={groupName}
              />
              {isRandomized && (
                <span className={styles['question-group-block-randomized-indicator']}>
                  The questions in this group are randomized.
                </span>
              )}
            </div>
          </div>

          {showActions && (
            <div className={styles['question-group-block-actions']}>
              <ActionOverlayPopover
                hasArrow={false}
                isActive={isDropdownActive}
                onClick={() => setIsDropdownActive(!isDropdownActive)}
                onHide={() => setIsDropdownActive(false)}
              >
                <ul>
                  <li>
                    <button
                      aria-label={
                        isRandomized ? 'Remove question randomization' : 'Randomize question order'
                      }
                      data-testid="randomize-question-order-button"
                      onClick={() => {
                        handleRandomizeToggle();
                        setIsDropdownActive(false);
                      }}
                    >
                      <ShuffleIcon />
                      {isRandomized ? 'Remove question randomization' : 'Randomize question order'}
                    </button>
                  </li>
                </ul>
              </ActionOverlayPopover>
            </div>
          )}
        </div>

        <div className={styles['question-group-block-questions']}>
          <Droppable
            droppableId={droppableId}
            isDropDisabled={isReadOnly || !showActions}
            type={droppableType}
          >
            {(droppableProvided) => (
              <div
                className={styles['question-group-block-droppable-zone']}
                ref={droppableProvided.innerRef}
                {...droppableProvided.droppableProps}
              >
                {questions.map((question, index) => {
                  const questionId = question.id ?? index;
                  // Question expects the flat index in the overall survey.questions array (drives
                  // disqualify eligibility, option-logic ordering, and the number fallback). Group
                  // members are contiguous, so flatIndexStart + local index gives it.
                  const flatIndex = flatIndexStart + index;
                  return (
                    <Draggable
                      draggableId={`question-group-question-${group.id}-${questionId}`}
                      index={index}
                      isDragDisabled={isReadOnly || !showActions}
                      key={questionId}
                    >
                      {(draggableProvided) => (
                        <Question
                          canPreviewSurvey={canPreviewSurvey}
                          firstErrorRef={firstErrorRef}
                          index={flatIndex}
                          isDetailedQuestionView={isDetailedQuestionView}
                          number={questionDisplayIndexById[question.id] ?? String(flatIndex + 1)}
                          onDuplicateQuestion={onDuplicateQuestion}
                          onEditQuestion={onEditQuestion}
                          onPreviewSurvey={onPreviewSurvey}
                          onRemoveFromGroup={handleRemoveFromGroup}
                          onRemoveQuestion={handleRemoveQuestionFromSurvey}
                          provided={draggableProvided}
                          question={question}
                          questionLogic={questionLogic}
                          selectedQuestions={selectedQuestions}
                          setQuestionLogic={setQuestionLogic}
                          showActions={showActions}
                        />
                      )}
                    </Draggable>
                  );
                })}
                {droppableProvided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      </Card>
    </div>
  );
};

QuestionGroupBlock.propTypes = {
  canPreviewSurvey: PropTypes.bool.isRequired,
  firstErrorRef: PropTypes.object.isRequired,
  flatIndexStart: PropTypes.number.isRequired,
  group: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    randomizeOrder: PropTypes.bool,
  }).isRequired,
  isDetailedQuestionView: PropTypes.bool.isRequired,
  onDuplicateQuestion: PropTypes.func.isRequired,
  onEditQuestion: PropTypes.func.isRequired,
  onPreviewSurvey: PropTypes.func.isRequired,
  onRemoveQuestion: PropTypes.func.isRequired,
  provided: PropTypes.object.isRequired,
  questionLogic: PropTypes.object,
  questions: PropTypes.array.isRequired,
  selectedQuestions: PropTypes.array.isRequired,
  setQuestionLogic: PropTypes.func.isRequired,
  showActions: PropTypes.bool,
};

export default QuestionGroupBlock;
